/**
 * Leitura atrasada — o degrau ANTES do "sem sinal" (C-24, decisão do dono de 20/10/2026).
 * TS puro.
 *
 * Escada: o instrumento passa da janela de frescor do tipo → item secundário no sino
 * ("Para acompanhar"), sem botão de TARP, para não encher a tela. Quando o caminho crítico
 * assume (`semSinalVisivel`: banner + guia de TARP), o atraso sai e o crítico fica com ele.
 *
 * A régua é a da ingestão (`leituraForaDaJanela` = cadência + tolerância de entrega), e não
 * "passou da cadência": a GPX entrega com 30–40 min de atraso o tempo todo, e acusar a cada
 * cadência perdida gerou 955 eventos falsos em 14 dias de produção.
 *
 * Calculado de `lastReadingAt`, nunca de `isOnline`: a ingestão só julga quem veio no lote,
 * e um combo que falha por dias congela o `isOnline` em `true`. É justamente esse
 * instrumento que mais precisa aparecer aqui.
 */
import { cadenciaEsperadaMin, ehQuedaDeFeed, janelaFrescorMin } from '../instrumentos/gpx-monitoramento';
import { foraDeServico } from '../instrumentos/fora-de-servico';
import { semSinalVisivel } from '../instrumentos/nivel-exibido';
import { leituraForaDaJanela, tipoApiDeInstrumento } from '../instrumentos/perda-de-sinal';
import type { Instrument, InstrumentType } from '../types';

/**
 * Tipos que publicam leitura curada pela GPX numa cadência MEDIDA — os únicos em que
 * "deveria ter chegado" tem sentido.
 *
 * Fora: MS/MSE (campanha topográfica mensal, chega pela planilha do SharePoint), EM (a
 * curadoria não produz valor classificável e `lastReadingAt` nunca avança — ficaria
 * eternamente atrasada) e INC (sem cadência medida; cairia no default de 12 h).
 *
 * Não usa `vemDaGpx`: ela ainda marca o tiltímetro como não-GPX (comentário anterior ao
 * GUPM-305) e marca EM/INC como GPX.
 */
const TIPOS_COM_CADENCIA_GPX: ReadonlySet<InstrumentType> = new Set(['INA', 'PZ', 'TILT', 'RL', 'MV', 'PLV']);

/** Um instrumento cuja leitura deveria ter chegado e não chegou. */
export interface InstrumentoAtrasado {
  instrument: Instrument;
  /** Código da estrutura (`dam.code`); `null` quando a estrutura não está na lista. */
  damCode: string | null;
  ultimaLeitura: Date;
  /** Cadência esperada de publicação do tipo, em minutos. */
  cadenciaMin: number;
  /** Até quando a próxima leitura deveria ter chegado: última + janela de frescor. */
  prazo: Date;
}

/**
 * Um item de atraso no sino: um instrumento, ou a estrutura inteira quando a causa provável
 * é o envio da GPX e não os sensores.
 */
export type ItemDeAtraso =
  | ({ kind: 'instrumento' } & InstrumentoAtrasado)
  | {
      kind: 'feed';
      damId: string;
      damCode: string | null;
      /** Os atrasados da estrutura, o primeiro a perder o prazo primeiro. */
      atrasados: InstrumentoAtrasado[];
      /** Quantos instrumentos elegíveis a estrutura tem. */
      total: number;
    };

function elegivel(inst: Instrument): inst is Instrument & { lastReadingAt: Date } {
  return inst.isActive
    && !foraDeServico(inst)
    && inst.lastReadingAt !== null
    && TIPOS_COM_CADENCIA_GPX.has(inst.type);
}

/**
 * Instrumentos com leitura atrasada agora.
 *
 * A queda coletiva é decidida por **estrutura** (decisão do dono, 20/10/2026): com
 * `ehQuedaDeFeed` (≥5 elegíveis e ≥50% atrasados, o mesmo limiar da ingestão) a estrutura
 * vira um item só. O recorte não é o combo estrutura × tipo da ingestão: as Barragens A e
 * C têm 2–4 instrumentos por tipo, nunca passam do piso de 5, e no apagão geral o sino
 * ficava com 22 itens soltos (banco espelho de 27/09).
 *
 * Consequência aceita de misturar cadências (30 min do TIL a 12 h do MV) num recorte só:
 * num apagão real, a estrutura só passa de 50% quando os INA/PZ vencem (~6h45 depois). Até
 * lá, TIL/RL/PLV aparecem como itens soltos e depois se juntam no item de feed.
 */
export function computeAtrasos(
  instruments: readonly Instrument[],
  dams: readonly { id: string; code: string }[],
  agora: Date,
): ItemDeAtraso[] {
  const codePorDam = new Map(dams.map((d) => [d.id, d.code]));
  const grupos = new Map<string, { elegiveis: number; atrasados: InstrumentoAtrasado[]; statusEmDia: boolean[] }>();

  for (const inst of instruments) {
    // O "sem sinal" crítico é dono do instrumento — com ou sem TARP suprimindo o card. Fica
    // fora também da conta da queda: contado só no denominador, uma estrutura com 2 TILT
    // mudos e 1 INA atrasado virava "Feed GPX: 1 de 5" (achado da revisão).
    if (!elegivel(inst) || semSinalVisivel(inst)) continue;
    const grupo = grupos.get(inst.damId) ?? { elegiveis: 0, atrasados: [], statusEmDia: [] };
    grupos.set(inst.damId, grupo);
    grupo.elegiveis += 1;

    const atrasado = leituraForaDaJanela(inst.lastReadingAt, inst.type, agora);
    grupo.statusEmDia.push(!atrasado);
    if (!atrasado) continue;

    const tipoApi = tipoApiDeInstrumento(inst.type);
    grupo.atrasados.push({
      instrument: inst,
      damCode: codePorDam.get(inst.damId) ?? null,
      ultimaLeitura: inst.lastReadingAt,
      cadenciaMin: cadenciaEsperadaMin(tipoApi),
      prazo: new Date(inst.lastReadingAt.getTime() + janelaFrescorMin(tipoApi) * 60_000),
    });
  }

  const itens: ItemDeAtraso[] = [];
  for (const [damId, g] of grupos) {
    if (g.atrasados.length === 0) continue;
    const atrasados = [...g.atrasados].sort((a, b) => a.prazo.getTime() - b.prazo.getTime());
    if (ehQuedaDeFeed(g.statusEmDia)) {
      itens.push({
        kind: 'feed',
        damId,
        damCode: codePorDam.get(damId) ?? null,
        atrasados,
        total: g.elegiveis,
      });
    } else {
      for (const a of atrasados) itens.push({ kind: 'instrumento', ...a });
    }
  }
  return itens;
}
